import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid2,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  CircularProgress,
  Fade,
  Stack,
  TextField,
  InputAdornment,
  Divider,
  Popover,
} from '@mui/material';
import {
  CheckRounded,
  CloseRounded,
  PeopleOutlined,
  PersonAddOutlined,
  SearchRounded,
  GroupsOutlined,
  KeyboardArrowDownRounded,
  CheckRounded as CheckIcon,
  DoneAllOutlined,
} from '@mui/icons-material';
import API from '../api/api';
import { useStudentGroups } from '../Hooks/useGetStudentGroups';
import { useGetStudents } from '../Hooks/useGetStudents';
import { useGetStudentAttendies } from '../Hooks/useGetStudentAttendies';
import AddStudent from '../Components/Dialogs/AddStudent';
import useGetSubjects from '../Hooks/useGetSubjects';
import { useGetSessions } from '../Hooks/useGetSessions';

// ─── Constants ────────────────────────────────────────────────────────────────

const AVATAR_PALETTE = [
  { bg: 'rgba(59,130,246,0.25)',  color: '#93c5fd' },
  { bg: 'rgba(34,197,94,0.2)',   color: '#4ade80' },
  { bg: 'rgba(168,85,247,0.2)',  color: '#c084fc' },
  { bg: 'rgba(251,146,60,0.2)',  color: '#fb923c' },
  { bg: 'rgba(236,72,153,0.2)',  color: '#f472b6' },
  { bg: 'rgba(20,184,166,0.2)',  color: '#2dd4bf' },
  { bg: 'rgba(234,179,8,0.2)',   color: '#fbbf24' },
  { bg: 'rgba(239,68,68,0.2)',   color: '#f87171' },
];

function colorFor(index) {
  return AVATAR_PALETTE[index % AVATAR_PALETTE.length];
}

function initials(student) {
  return `${student.first_name?.[0] ?? ''}${student.last_name?.[0] ?? ''}`.toUpperCase();
}

function formatTime(isoString) {
  if (!isoString) return null;
  return new Date(isoString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ─── Shared subject grid ──────────────────────────────────────────────────────

function SubjectGrid({ selected, onSelect, subjects}) {
  return (
    <Grid2 container spacing={0.75}>
      {subjects.map((sub) => (
        <Grid2 key={sub.id} size={{ xs: 6 }}>
          <Box
            onClick={() => onSelect(sub.id)}
            sx={{
              py: 1.25, px: 1, borderRadius: 1.5, border: '1px solid',
              borderColor: selected === sub.id
                ? 'rgba(147,197,253,0.5)' : 'rgba(255,255,255,0.08)',
              bgcolor: selected === sub.id
                ? 'rgba(30,64,175,0.3)' : 'rgba(255,255,255,0.03)',
              color: selected === sub.id ? '#93c5fd' : '#94a3b8',
              textAlign: 'center', fontSize: 13, cursor: 'pointer',
              transition: 'all 0.12s',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.18)' },
            }}
          >
            {sub.title}
          </Box>
        </Grid2>
      ))}
    </Grid2>
  );
}

// ─── Live clock ───────────────────────────────────────────────────────────────

function LiveClock() {
  const [time, setTime] = useState(() =>
    new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' })
  );
  useEffect(() => {
    const id = setInterval(() =>
      setTime(new Date().toLocaleTimeString('en-US', {
        hour: 'numeric', minute: '2-digit', second: '2-digit',
      })), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <Typography sx={{ fontSize: 13, color: '#64748b', fontFamily: 'monospace' }}>
      {time}
    </Typography>
  );
}

// ─── Student card ─────────────────────────────────────────────────────────────

function StudentCard({ student, index, onClick }) {
  const { bg, color } = colorFor(index);
  const signedIn = !!student.signed_in;
  const signedOut = !!student.signed_out;
  const inactive = !student.active;

  return (
    <Box
      onClick={inactive ? undefined : () => onClick(student, index)}
      sx={{
        position: 'relative',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
        p: 2, borderRadius: 3, border: '1px solid',
        borderColor: signedIn ? 'rgba(74,222,128,0.3)' : 'rgba(255,255,255,0.08)',
        bgcolor: signedIn ? 'rgba(34,197,94,0.08)' : 'rgba(255,255,255,0.04)',
        cursor: inactive ? 'default' : 'pointer',
        opacity: inactive ? 0.38 : 1,
        transition: 'all 0.15s', textAlign: 'center', userSelect: 'none',
        '&:hover': inactive ? {} : {
          bgcolor: signedIn ? 'rgba(34,197,94,0.13)' : 'rgba(255,255,255,0.09)',
          borderColor: signedIn ? 'rgba(74,222,128,0.5)' : 'rgba(255,255,255,0.18)',
          transform: 'translateY(-2px)',
        },
        '&:active': inactive ? {} : { transform: 'scale(0.98)' },
      }}
    >
      {signedIn && (
        <Box sx={{
          position: 'absolute', top: 8, right: 8, width: 18, height: 18,
          borderRadius: '50%', bgcolor: '#22c55e',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <CheckRounded sx={{ fontSize: 11, color: '#fff' }} />
        </Box>
      )}

      <Box sx={{
        width: 56, height: 56, borderRadius: '50%', bgcolor: bg, color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 18, fontWeight: 500, flexShrink: 0,
      }}>
        {initials(student)}
      </Box>

      <Box>
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#f1f5f9', lineHeight: 1.3 }}>
          {student.first_name} {student.last_name}
        </Typography>
        <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.25 }}>
          Grade {student.grade_level}
        </Typography>
      </Box>

      {signedIn && student.signed_time && (
        <Chip
          icon={<CheckRounded sx={{ fontSize: '10px !important', color: '#4ade80 !important' }} />}
          label={formatTime(student.signed_time) ?? student.signed_time}
          size="small"
          sx={{
            fontSize: 10, height: 20,
            bgcolor: 'rgba(34,197,94,0.15)', color: '#4ade80', border: 'none',
            '& .MuiChip-icon': { ml: '4px' },
          }}
        />
      )}
      {!signedIn && student.check_out_time && (
        <Chip
        icon={<DoneAllOutlined sx={{ fontSize: '10px !important', color: '#94a3b8 !important' }} />}
        label={`Out ${formatTime(student.check_out_time)}`}
        size="small"
        sx={{
          fontSize: 10, height: 20,
          bgcolor: 'rgba(148,163,184,0.12)',
          color: '#94a3b8',
          border: 'none',
          '& .MuiChip-icon': { ml: '4px' },
        }}
      />
      )}
    </Box>
  );
}

// ─── Sign-in / sign-out dialog ────────────────────────────────────────────────

function SignInDialog({ student, index, open, onClose, onConfirm, loading, subjects}) {
  const [selectedSubject, setSelectedSubject] = useState(null);

  useEffect(() => {
    if (open) setSelectedSubject(null);
  }, [open, student?.id]);

  if (!student) return null;
  const { bg, color } = colorFor(index);
  const signingOut = !!student.signed_in;
  const signedOut = student.check_out;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { bgcolor: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3, backgroundImage: 'none' } }}
      BackdropProps={{ sx: { bgcolor: 'rgba(0,0,0,0.75)' } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, pt: 2.5, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <Box sx={{ width: 46, height: 46, borderRadius: '50%', bgcolor: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 500, flexShrink: 0 }}>
          {initials(student)}
        </Box>
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 500, color: '#f1f5f9' }}>
            {student.first_name} {student.last_name}
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            {signingOut ? `Signed in ${formatTime(student.signed_time) ?? ''} · tap to sign out` : `Grade ${student.grade_level}`}
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ px: 2.5, pt: 2, pb: 1 }}>
        {!signingOut && (
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', mb: 1 }}>
              Select a subject
            </Typography>
            <SubjectGrid subjects={subjects} selected={selectedSubject} onSelect={setSelectedSubject} />
          </Box>
        )}
        {signingOut && (
          <Box sx={{ py: 1.5, px: 2, borderRadius: 1.5, bgcolor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
            <Typography sx={{ fontSize: 13, color: '#fca5a5', lineHeight: 1.6 }}>
              This will mark you as signed out for today's session.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{ flex: 1, color: '#94a3b8', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1.5, textTransform: 'none', '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' } }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(student, selectedSubject, signingOut)}
          disabled={loading || (!signingOut && !selectedSubject) || signedOut}
          sx={{ flex: 2, borderRadius: 1.5, textTransform: 'none', fontWeight: 500, bgcolor: signingOut ? '#7f1d1d' : '#1d4ed8', color: '#fff', '&:hover': { bgcolor: signingOut ? '#991b1b' : '#1e40af' }, '&:disabled': { bgcolor: 'rgba(255,255,255,0.06)', color: '#475569' } }}
          startIcon={loading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : signingOut ? <CloseRounded sx={{ fontSize: 16 }} /> : <CheckRounded sx={{ fontSize: 16 }} />}
        >
          {loading ? 'Saving…' : signingOut ? 'Sign out' : 'Sign in'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}



// ─── Group filter button + popover ───────────────────────────────────────────
//
// Props:
//   groups          — [{ id, name }] fetched from the API
//   activeGroupId   — currently selected group id, or null (= All students)
//   loading         — true while fetching groups or filtered students
//   onSelect(id)    — called with group id, or null for "All students"

function GroupFilterButton({ groups = [], activeGroupId, loading, onSelect, disable = false }) {
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);

  const activeGroup = groups.find((g) => g.id === activeGroupId) ?? null;
  if (disable) { return ;}

  return (
    <>
      {/* Trigger button — same ghost style as "Add student" */}
      <Box
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.75,
          px: 1.25, py: 0.6,
          border: '1px solid',
          borderColor: activeGroup
            ? 'rgba(167,139,250,0.45)'   // purple accent when a group is active
            : 'rgba(255,255,255,0.12)',
          borderRadius: 1.5,
          bgcolor: activeGroup
            ? 'rgba(109,40,217,0.18)'
            : 'rgba(255,255,255,0.05)',
          cursor: 'pointer',
          transition: 'all 0.15s',
          '&:hover': {
            bgcolor: activeGroup ? 'rgba(109,40,217,0.28)' : 'rgba(255,255,255,0.09)',
            borderColor: activeGroup ? 'rgba(167,139,250,0.65)' : 'rgba(255,255,255,0.22)',
          },
          '&:active': { transform: 'scale(0.97)' },
        }}
      >
        {loading
          ? <CircularProgress size={13} sx={{ color: '#a78bfa' }} />
          : <GroupsOutlined sx={{ fontSize: 15, color: activeGroup ? '#a78bfa' : '#94a3b8' }} />
        }
        <Typography sx={{
          fontSize: 12, fontWeight: 500,
          color: activeGroup ? '#a78bfa' : '#94a3b8',
          maxWidth: 140,
          overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
        }}>
          {activeGroup ? activeGroup.name : 'All students'}
        </Typography>
        <KeyboardArrowDownRounded
          sx={{
            fontSize: 15,
            color: activeGroup ? '#a78bfa' : '#64748b',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s',
          }}
        />
      </Box>

      {/* Popover menu */}
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 0.75,
            bgcolor: '#1e293b',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 2,
            backgroundImage: 'none',
            minWidth: 200,
            maxWidth: 280,
            overflow: 'hidden',
          },
        }}
      >
        {/* "All students" row */}
        <Box
          onClick={() => { onSelect(null); setAnchor(null); }}
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 1.75, py: 1.1, cursor: 'pointer',
            bgcolor: activeGroupId === null ? 'rgba(255,255,255,0.06)' : 'transparent',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            transition: 'background 0.12s',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.07)' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleOutlined sx={{ fontSize: 15, color: '#64748b' }} />
            <Typography sx={{ fontSize: 13, color: '#f1f5f9', fontWeight: activeGroupId === null ? 500 : 400 }}>
              All students
            </Typography>
          </Box>
          {activeGroupId === null && (
            <CheckRounded sx={{ fontSize: 14, color: '#4ade80' }} />
          )}
        </Box>

        {/* Group rows */}
        {groups.length === 0 && (
          <Box sx={{ px: 1.75, py: 1.5 }}>
            <Typography sx={{ fontSize: 12, color: '#475569', textAlign: 'center' }}>
              No groups found for this location
            </Typography>
          </Box>
        )}
        {groups.map((group, i) => (
          <Box
            key={group.id}
            onClick={() => { onSelect(group.id); setAnchor(null); }}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              px: 1.75, py: 1.1, cursor: 'pointer',
              bgcolor: activeGroupId === group.id ? 'rgba(109,40,217,0.15)' : 'transparent',
              borderBottom: i < groups.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
              transition: 'background 0.12s',
              '&:hover': { bgcolor: activeGroupId === group.id ? 'rgba(109,40,217,0.22)' : 'rgba(255,255,255,0.06)' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <GroupsOutlined sx={{ fontSize: 15, color: activeGroupId === group.id ? '#a78bfa' : '#64748b', flexShrink: 0 }} />
              <Typography sx={{
                fontSize: 13,
                color: activeGroupId === group.id ? '#a78bfa' : '#cbd5e1',
                fontWeight: activeGroupId === group.id ? 500 : 400,
                overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
              }}>
                {group.title}
              </Typography>
            </Box>
            {activeGroupId === group.id && (
              <CheckRounded sx={{ fontSize: 14, color: '#a78bfa', flexShrink: 0, ml: 1 }} />
            )}
          </Box>
        ))}
      </Popover>
    </>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function DelegateSigninPage() {
    const queryString = window.location.search;

    // 2. Parse it using the built-in URLSearchParams
    const urlParams = new URLSearchParams(queryString);

    // 3. Extract the values (mapping snake_case to your variables)
    const session_id = urlParams.get('session_id');
    const location_id = urlParams.get('location_id');
    const tutor_id = urlParams.get('tutor_id');
    const semester_id = urlParams.get('semester_id');

    const [students,      setStudents]      = useState([]);
    const [pageLoading,   setPageLoading]   = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [validSession,    setValidSession]    = useState(false);

    // ── Student groups ──────────────────────────────────────────────────────────
    const [groups,          setGroups]          = useState([]);
    const [activeGroupId,   setActiveGroupId]   = useState(null);
    const [groupLoading,    setGroupLoading]    = useState(false);

    const [dialogOpen,    setDialogOpen]    = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [activeStudent, setActiveStudent] = useState(null);
    const [activeIndex,   setActiveIndex]   = useState(0);
    const [initData, setInitData ] = useState({location_id: null, semester_id: null, active: null});

    const { studentsGroups, loading: loading_student_groups, fetchStudentGroups } = useStudentGroups();
    const { loading: loading_students, fetchStudents } = useGetStudents();
    const { loading: loading_student_attendies, fetchStudentsAttendies} = useGetStudentAttendies();
    const { subjects, refetch: fetchSubjects } = useGetSubjects();
    const { checkCompletedSession } = useGetSessions()
    const [attendanceMap, setMap] = useState(null);

    const pollRef = useRef(null);
    // ── Fetch students ──────────────────────────────────────────────────────────

    useEffect(() => {
      let isMounted = true; // Prevents state updates on unmounted components
  
      async function startInitialization() {
          if (!session_id) return;
          
          setPageLoading(true);
  
          try {
              // 1. Wait for validation properly
              const status = await checkCompletedSession(session_id);
              if (status || !isMounted) {
                  setPageLoading(false);
                  setValidSession(status);
                  return;
              }
  
              // 2. Fire off independent metadata fetches (don't need to await them for the main list)
              if (location_id && tutor_id && semester_id) {
                  fetchStudentGroups(location_id, tutor_id, semester_id);
              }
              fetchSubjects();
  
              // 3. Fetch core data in parallel
              const [rawStudents, attendanceMap] = await Promise.all([
                  location_id ? fetchStudents(location_id) : Promise.resolve([]),
                  getSessionStudents(),
              ]);
  
              if (isMounted) {
                  setMap(attendanceMap);
                  setStudents(mergeAttendance(rawStudents ?? [], attendanceMap));
              }
          } catch (error) {
              console.error("Initialization failed", error);
          } finally {
              if (isMounted) setPageLoading(false);
          }
      }
  
      startInitialization();
  
      // Cleanup function
      return () => {
          isMounted = false;
      };
  }, [session_id, location_id, tutor_id, semester_id]);
  

    const handleGroupSelect = async (id) => {
        if (validSession) { return; }
        if (id  === null || id === undefined) {
            setActiveGroupId(null);
            const students = await fetchStudents(location_id);
            setStudents(mergeAttendance( students?? [], attendanceMap));
            return
        }else {
            setActiveGroupId(id);
            setGroupLoading(true);
            const student_attendies = await fetchStudentsAttendies(id);
            setStudents(mergeAttendance( student_attendies ?? [], attendanceMap));
            setGroupLoading(false);
        }
    }

  // ── Sign in / sign out ──────────────────────────────────────────────────────
    function handleCardClick(student, index) {
        setActiveStudent(student);
        setActiveIndex(index);
        setDialogOpen(true);
    }

    async function handleConfirm(student, subjectId, isSigningOut) {
        setActionLoading(true);
        try {
            await API.post('/sync_student', {
                student_id:  student.id,
                session_id:  parseInt(session_id),
                subject_id:  isSigningOut ? undefined : subjectId,
                check_in:   isSigningOut ? false : true,
                check_out:  isSigningOut ? true : false,
                check_in_time:  isSigningOut ? undefined : new Date().toISOString(),
                check_out_time: isSigningOut ? new Date().toISOString() : undefined,
                location_id: location_id ? parseInt(location_id) : undefined,
            });
            setStudents((prev) =>
                prev.map((s) =>
                s.id === student.id
                    ? { ...s, signed_in: !isSigningOut, signed_time: isSigningOut ? null : new Date().toISOString(), subject_id: isSigningOut ? null : subjectId }
                    : s
                )
            );
            setDialogOpen(false);
            setTimeout(() => fetchStudents(true), 600);
        } catch (err) {
            console.error('update_student failed', err);
        } finally {
            setActionLoading(false);
        }
    }

    // --- Merger ---
    function mergeAttendance(students, attendanceMap) {
        return students.map((s) => {
            const record = attendanceMap[s.id];
            if (!record) return { ...s, signed_in: false, signed_time: null, subject_id: null };
            return { ...s, ...record };
        });
    }

    // --- Source of truth ----
    async function getSessionStudents() {
        try {
            const response = await API.get("/get_session_students", {
                params: { session_id: parseInt(session_id) }
            });
            const records = response.data?.data ?? [];
    
            // Build a lookup: { 5: { signed_in: true, signed_time: "...", subject_id: 1 }, ... }
            const attendanceMap = {};
            records.forEach((r) => {
                attendanceMap[r.student_id] = {
                    signed_in:      r.check_in && !r.check_out,
                    signed_time:    r.check_in_time,
                    check_out_time: r.check_out_time,
                    check_out: r.check_out,
                    subject_id:     r.subject_id,
                };
            });
            return attendanceMap;
        } catch (err) {
            console.error('getSessionStudents failed', err);
            return {};
        }
    }

    const handleAddStudentClose = async() => {
        if (!validSession) { setAddDialogOpen(false); setInitData({active: null, location_id: null, semester_id: null}); return; }
        if (activeGroupId !== null) {
            setAddDialogOpen(false);
            const students = await fetchStudentsAttendies(activeGroupId);
            setStudents(students);
            setInitData({active: null, location_id: null, semester_id: null});
            return;
        }
        else {
            const students = await fetchStudents(location_id);
            setStudents(students);
            setAddDialogOpen(false);
            setInitData({active: null, location_id: null, semester_id: null});
            return;
        }
    }


  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0f172a', display: 'flex', flexDirection: 'column', px: { xs: 2, sm: 3 }, pt: 2.5, pb: 4 }}>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: '#1e40af', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PeopleOutlined sx={{ fontSize: 20, color: '#93c5fd' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 17, fontWeight: 500, color: '#f1f5f9' }}>Student Sign-In</Typography>
            <Typography sx={{ fontSize: 12, color: '#475569' }}>
              Session {session_id ?? '—'}{location_id ? ` · Location ${location_id}` : ''}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <LiveClock />
          {/* Group filter dropdown */}
          <GroupFilterButton
            disable={validSession}
            groups={studentsGroups}
            activeGroupId={activeGroupId}
            loading={groupLoading}
            onSelect={handleGroupSelect}
          />
          <Chip
            label="After School Tutoring"
            size="small"
            sx={{ fontSize: 11, bgcolor: 'rgba(30,64,175,0.25)', color: '#93c5fd', border: '1px solid rgba(147,197,253,0.2)' }}
          />

          {/* Add student button */}
          <Box
            onClick={() => {
              setInitData({location_id: Number(location_id), semester_id: Number(semester_id), active: true});
              setAddDialogOpen(true);

            }}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.75,
              px: 1.25, py: 0.6,
              border: '1px solid rgba(147,197,253,0.25)',
              borderRadius: 1.5,
              bgcolor: 'rgba(30,64,175,0.15)',
              cursor: 'pointer',
              transition: 'all 0.15s',
              '&:hover': { bgcolor: 'rgba(30,64,175,0.3)', borderColor: 'rgba(147,197,253,0.45)' },
              '&:active': { transform: 'scale(0.97)' },
            }}
          >
            <PersonAddOutlined sx={{ fontSize: 15, color: '#93c5fd' }} />
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#93c5fd' }}>
              Add student
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* ── Prompt ── */}
      <Box sx={{ textAlign: 'center', mb: 2.5 }}>
        <Typography sx={{ fontSize: 24, fontWeight: 500, color: '#f1f5f9', mb: 0.5 }}>
          Tap your name to sign in
        </Typography>
        <Typography sx={{ fontSize: 14, color: '#64748b' }}>
          Select a subject when prompted, then tap OK
        </Typography>
      </Box>

     

      {/* ── Student grid ── */}
      {pageLoading || loading_students ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress sx={{ color: '#3b82f6' }} />
        </Box>
      ) : (
        <Fade in>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1.25 }}>
            {students?.map((student, index) => {
              if (!student.active) { return; }
              return (
                <StudentCard key={student.id} student={student} index={index} onClick={handleCardClick} />
              )
            })}
          </Box>
        </Fade>
      )}

      {/* ── Dialogs ── */}
      <SignInDialog
        open={dialogOpen}
        student={activeStudent}
        index={activeIndex}
        subjects={subjects}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirm}
        loading={actionLoading}
      />

      <AddStudent
        disable={validSession}
        open={addDialogOpen}
        onClose={handleAddStudentClose}
        initialData={{...initData}}
        minimal
      />
    </Box>
  );
}